import type { ReactNode } from 'react';
import { defineMessages, FormattedMessage, type IntlShape } from 'react-intl';
import {
  PROTECTED_ACTION_CATEGORIES,
  REQUEST_PROTECTED_ACTION_APPROVAL_TOOL_NAME,
  type ProtectedActionCategory,
} from '../../../contracts/child-run-approval.types';
import type { ApprovalOverrideScope } from '../../../contracts/harness.types';
import { REQUEST_HUMAN_APPROVAL_TOOL_NAME } from '../../../contracts/projects/approval-tool-names';
import type { ToolCallDisplay } from '../../../contracts/tool-call-display';

import { readMultiplayerApprovalLabel } from '../../../data/projects/components/ApprovalRequestBlock.multiplayer';
import { readPresentation } from '../../inline-approval/tool-call-presentation';

// Re-exported rather than declared: `approval-note-opt-in.ts` reads the same tool
// name from `/data`, which may not reach this zone, so the declaration sits in
// `/contracts`. This path publishes exactly the names it always published.
export { REQUEST_HUMAN_APPROVAL_TOOL_NAME };
export const MERGE_PATIENT_PROFILES_TOOL_NAME = 'merge_patient_profiles';
export const MERGE_SESSIONS_TOOL_NAME = 'merge_sessions';

export interface ApprovalRequestInput {
  approval_id: string;
  tool_name: string;
  tool_input: Record<string, unknown>;
  tool_call_id?: string;
  /** Backend-owned display; its title drives the human-readable tool label. */
  toolCallDisplay?: ToolCallDisplay;
  /** Resolve scopes the BE advertises for this approval; `session` enables the per-chat label. */
  available_override_scopes?: ApprovalOverrideScope[];
  /**
   * Server-authored approval context (`project_tool_approval_context`). Carries
   * the card's `presentation`, which the FE renders instead of the redacted
   * `tool_input`. Server-derived so it survives the LLM-key redaction that
   * strips `tool_input`.
   */
  context?: Record<string, unknown>;
  /**
   * The live approval event's context, which carries a session edit's text
   * before the snapshot's context has loaded.
   */
  pendingContext?: Record<string, unknown>;
}

/**
 * One per-field row of a note-push approval (the presentation's
 * `note_push_rows` block): the target EMR field, the Heidi sections that
 * combine into it, and the EXACT text the child will write to it.
 */
export interface NotePushApprovalRow {
  /** Opaque `AxSelector` wire-JSON string for the target EMR field. */
  selector: string;
  /** Heidi template sections that combine into this field. */
  section_headings: string[];
  /** The exact combined content the child writes to this field. */
  prepared_input: string;
}

export interface ApprovalDisplayFields {
  reason?: string;
  proposedAction?: string;
  riskLevel?: 'low' | 'medium' | 'high';
  /**
   * Backend-owned tool label for the title/subtitle: the tool's fixed
   * `presentation.title`, else the model-written `tool_call_display.title`.
   */
  toolLabel?: string;
}

/**
 * Title/subtitle fields only — body content is the approval-input registry's
 * job (`ApprovalInputDetails`), where showing a tool's input is a per-tool opt-in.
 */
export const readApprovalFields = (
  approval: ApprovalRequestInput,
  intl: IntlShape
): ApprovalDisplayFields => {
  const input = approval.tool_input ?? {};
  const toolLabel =
    readPresentation(approval.context ?? approval.pendingContext).title ??
    approval.toolCallDisplay?.title;
  if (approval.tool_name === REQUEST_HUMAN_APPROVAL_TOOL_NAME) {
    return {
      reason: typeof input.reason === 'string' ? input.reason : undefined,
      proposedAction:
        typeof input.proposed_action === 'string' ? input.proposed_action : undefined,
      riskLevel: normaliseRiskLevel(input.risk_level),
      toolLabel,
    };
  }

  if (approval.tool_name === REQUEST_PROTECTED_ACTION_APPROVAL_TOOL_NAME) {
    const { action, category } = input;
    return {
      proposedAction:
        typeof action === 'string' && action.trim() ? action.trim() : undefined,
      riskLevel: 'high',
      toolLabel: isProtectedActionCategory(category)
        ? intl.formatMessage(protectedActionCategoryMessages[category])
        : toolLabel,
    };
  }

  // The curated multiplayer label outranks the backend display.
  return {
    toolLabel: readMultiplayerApprovalLabel(intl, approval.tool_name) ?? toolLabel,
  };
};

const protectedActionCategoryMessages = defineMessages<ProtectedActionCategory>({
  prescribing: {
    defaultMessage: 'Prescribing',
    id: 'HCTdqS43P+',
    description: 'Risk category label for an action that prescribes medication',
  },
  destructive_ehr: {
    defaultMessage: 'Deleting or overwriting EHR data',
    id: 'KORFpQ888U',
    description:
      'Risk category label for an action that deletes or overwrites electronic health record data',
  },
  money_or_claim: {
    defaultMessage: 'Payment or claim',
    id: '/nLffMU6XD',
    description: 'Risk category label for an action that moves money or submits a claim',
  },
});

const isProtectedActionCategory = (value: unknown): value is ProtectedActionCategory =>
  PROTECTED_ACTION_CATEGORIES.some((category) => category === value);

export const buildTitle = (
  fields: ApprovalDisplayFields,
  isProactive: boolean,
  toolName: string,
  intl: IntlShape
): string => {
  if (fields.proposedAction) return fields.proposedAction;
  if (isProactive) {
    return intl.formatMessage({
      defaultMessage: 'Human approval requested',
      id: 'tCMNDpdPQY',
    });
  }
  return intl.formatMessage(
    {
      defaultMessage: 'Approval required: {tool}',
      id: 'NLs8ZGgJrK',
    },
    { tool: toolFamilyLabel(fields.toolLabel, toolName) }
  );
};

const toolFamilyLabel = (toolLabel: string | undefined, toolName: string): string =>
  toolLabel ? capitalize(toolLabel) : humanizeToolName(toolName);

/** `create_template` / `createTemplate` -> `Create template`. */
const humanizeToolName = (toolName: string): string => {
  const spaced = toolName
    .replace(/_+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim();
  return spaced ? capitalize(spaced.toLowerCase()) : toolName;
};

export const buildSubtitle = (
  fields: ApprovalDisplayFields,
  isProactive: boolean,
  toolName: string,
  intl: IntlShape
): ReactNode => {
  const family = isProactive
    ? intl.formatMessage({
        defaultMessage: 'Human approval',
        id: 'ARMjl/HfUY',
      })
    : toolFamilyLabel(fields.toolLabel, toolName);

  if (fields.riskLevel === 'high') {
    return (
      <FormattedMessage
        defaultMessage="{family} · <risk>High risk</risk>"
        id="M8jasC5wcN"
        description="Approval request family and high risk level"
        values={{
          family,
          risk: (chunks) => (
            <span className="font-medium text-validation-destructive">{chunks}</span>
          ),
        }}
      />
    );
  }
  if (fields.riskLevel === 'medium') {
    return (
      <FormattedMessage
        defaultMessage="{family} · <risk>Medium risk</risk>"
        id="pCt78VcBcf"
        description="Approval request family and medium risk level"
        values={{
          family,
          risk: (chunks) => (
            <span className="font-medium text-validation-warning">{chunks}</span>
          ),
        }}
      />
    );
  }
  // `buildTitle` already reads "Approval required: {family}" in this case.
  if (!isProactive && !fields.proposedAction) return undefined;
  return family;
};

/**
 * A single reconciled clinical field the agent is about to write onto the
 * kept profile (e.g. ``current_medications``). ``demographic_string`` is
 * pulled out separately as the kept-profile label and is not included here.
 */
export interface MergeReconciledField {
  key: string;
  value: string;
}

export interface MergeApprovalFields {
  /** Profile to KEEP (canonical). */
  targetId?: string;
  /** Duplicate profile that will be soft-deleted after its sessions move. */
  sourceId?: string;
  /** Corrected demographic label for the kept profile, if the agent set one. */
  demographicString?: string;
  /** Reconciled clinical fields, in clinician-reading order. */
  reconciled: MergeReconciledField[];
}

// Clinical fields the merge writes onto the kept profile, in the order a
// clinician would scan them. Mirrors ``_RECONCILABLE_INPUT_FIELDS`` in the
// backend ``patient_merge.py`` minus ``demographic_string`` (handled above).
const MERGE_RECONCILED_FIELD_KEYS = [
  'current_medications',
  'allergies',
  'past_medical_history',
  'additional_context',
] as const;

const readTrimmedString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined;

/**
 * Parse the ``merge_patient_profiles`` tool input into the structured shape the
 * clinician-friendly approval card renders. Unknown / empty fields are dropped
 * so the card only shows what the agent actually set.
 */
export const readMergeApprovalFields = (
  input: Record<string, unknown>
): MergeApprovalFields => {
  const reconciled: MergeReconciledField[] = [];
  for (const key of MERGE_RECONCILED_FIELD_KEYS) {
    const value = readTrimmedString(input[key]);
    if (value) reconciled.push({ key, value });
  }
  return {
    targetId: readTrimmedString(input.target_patient_profile_id),
    sourceId: readTrimmedString(input.source_patient_profile_id),
    demographicString: readTrimmedString(input.demographic_string),
    reconciled,
  };
};

export interface MergeSessionsApprovalFields {
  /** The anchor session the clinician pointed at (merged from). */
  anchorSessionId?: string;
  /** The other session ids being folded into the group with the anchor. */
  mergeSessionIds: string[];
}

/**
 * Parse the ``merge_sessions`` tool input into the shape the approval card
 * renders: the anchor ``session_id`` plus the ``session_ids`` being grouped
 * with it. Non-string / empty ids are dropped so the card only shows real ids.
 */
export const readMergeSessionsApprovalFields = (
  input: Record<string, unknown>
): MergeSessionsApprovalFields => {
  const rawIds = Array.isArray(input.session_ids) ? input.session_ids : [];
  const mergeSessionIds = rawIds
    .map((id) => readTrimmedString(id))
    .filter((id): id is string => Boolean(id));
  return {
    anchorSessionId: readTrimmedString(input.session_id),
    mergeSessionIds,
  };
};

const normaliseRiskLevel = (raw: unknown): 'low' | 'medium' | 'high' | undefined => {
  if (raw === 'low' || raw === 'medium' || raw === 'high') return raw;
  return undefined;
};

const capitalize = (value: string): string =>
  value.length === 0 ? value : value.charAt(0).toUpperCase() + value.slice(1);
